import type { ComputedRef, MaybeRefOrGetter } from 'vue'
import type { TransferVisualStatus } from '~/constants/receiptModel'
import type { ReceiptModel, ReceiptTransfer, TransferCategoryEnum } from '~/utils/receipt-model/ReceiptModel'
import { getTransferVisualStatus, PaymentModelStatus } from '~/constants/receiptModel'
import { formatSupplierName } from '~/utils/receipt-model/supplier'

export type ReceivableGroupKey = 'kit_invoice' | 'receipt_approval' | 'installation'

export type ReceivablesPanelState = 'no-values' | 'empty' | 'transfers'

export type ReceivablesTotalDisplay = 'analysis' | 'struck' | 'value'

export type ReceivablesI18nParams = Record<string, string | number>

export interface ReceivableRow {
  id: string
  number: number
  transfer: ReceiptTransfer
  value: number
  status: TransferVisualStatus
  descriptionKey: string
  descriptionParams: ReceivablesI18nParams
  canDownloadReceipt: boolean
}

export interface ReceivableGroup {
  key: ReceivableGroupKey
  labelKey: string
  rows: ReceivableRow[]
}

export interface FinancingDetailsLine {
  key: string
  labelKey: string
  labelParams: ReceivablesI18nParams
  value: number | null
}

export interface FinancingDetailsEquipmentLine extends FinancingDetailsLine {
  receiptTransfer: ReceiptTransfer | null
}

export interface FinancingDetailsViewModel {
  financingValue: number | null
  equipment: FinancingDetailsEquipmentLine | null
  servicesTitleKey: string
  services: FinancingDetailsLine[]
}

export interface UseReceivableValuesOptions {
  stepStatus?: MaybeRefOrGetter<string | null | undefined>
  supplierName?: MaybeRefOrGetter<string | null | undefined>
  isReimbursable?: MaybeRefOrGetter<boolean | null | undefined>
}

export interface UseReceivableValuesResult {
  state: ComputedRef<ReceivablesPanelState>
  groups: ComputedRef<ReceivableGroup[]>
  rows: ComputedRef<ReceivableRow[]>
  total: ComputedRef<number>
  totalDisplay: ComputedRef<ReceivablesTotalDisplay>
  isStepInAnalysis: ComputedRef<boolean>
  financingDetails: ComputedRef<FinancingDetailsViewModel>
}

const I18N_PREFIX = 'receipt-model.receivables'

interface GroupDefinition {
  key: ReceivableGroupKey
  labelKey: string
  matches: (transfer: ReceiptTransfer) => boolean
}

const GROUP_DEFINITIONS: GroupDefinition[] = [
  {
    key: 'kit_invoice',
    labelKey: `${I18N_PREFIX}.groups.kit_invoice`,
    matches: transfer => transfer.category === 'equipment',
  },
  {
    key: 'receipt_approval',
    labelKey: `${I18N_PREFIX}.groups.receipt_approval`,
    matches: transfer => transfer.category !== 'equipment' && transfer.kind === 'after_project_formalization',
  },
  {
    key: 'installation',
    labelKey: `${I18N_PREFIX}.groups.installation`,
    matches: transfer => transfer.category !== 'equipment' && transfer.kind === 'after_validation_installation',
  },
]

const CATEGORY_ORDER: Record<TransferCategoryEnum, number> = {
  equipment: 0,
  financing: 1,
  commission_financing: 2,
  commission_insurance: 3,
}

const CATEGORY_DESCRIPTION_KEYS: Partial<Record<TransferCategoryEnum, string>> = {
  commission_financing: `${I18N_PREFIX}.descriptions.commission_financing`,
  commission_insurance: `${I18N_PREFIX}.descriptions.commission_insurance`,
  equipment: `${I18N_PREFIX}.descriptions.kit_reimbursement`,
}

const STEP_STATUSES_IN_ANALYSIS: string[] = [
  PaymentModelStatus.UNDER_ANALYSIS,
]

const NEGATIVE_STATUSES: TransferVisualStatus[] = ['error', 'cancelled']

function getEquipmentValue(model: ReceiptModel | null): number {
  const equipmentValue = Number(model?.equipment_value) || 0
  if (equipmentValue > 0)
    return equipmentValue

  const equipmentTransfer = (model?.transfers ?? [])
    .find(transfer => transfer.category === 'equipment' && Number(transfer.value) > 0)

  return Number(equipmentTransfer?.value) || 0
}

function getKitLabelKey(isReimbursable: boolean, hasSupplierName: boolean): string {
  if (isReimbursable)
    return hasSupplierName ? `${I18N_PREFIX}.details.kit_reimbursable` : `${I18N_PREFIX}.details.kit_reimbursable_unnamed`

  return hasSupplierName ? `${I18N_PREFIX}.details.kit` : `${I18N_PREFIX}.details.kit_unnamed`
}

function getCategoryOrder(transfer: ReceiptTransfer): number {
  return CATEGORY_ORDER[transfer.category] ?? Number.MAX_SAFE_INTEGER
}

function sortByCategory(transfers: ReceiptTransfer[]): ReceiptTransfer[] {
  return [...transfers].sort((a, b) => getCategoryOrder(a) - getCategoryOrder(b))
}

function sumValues(transfers: ReceiptTransfer[]): number {
  return transfers.reduce((acc, transfer) => acc + (Number(transfer.value) || 0), 0)
}

function getDescription(
  transfer: ReceiptTransfer,
  financingIndex: number,
  financingCount: number,
): Pick<ReceivableRow, 'descriptionKey' | 'descriptionParams'> {
  if (transfer.category === 'financing') {
    if (financingCount === 1)
      return { descriptionKey: `${I18N_PREFIX}.descriptions.services_single`, descriptionParams: {} }

    return {
      descriptionKey: `${I18N_PREFIX}.descriptions.services_installment`,
      descriptionParams: { index: financingIndex, total: financingCount, percent: transfer.percent },
    }
  }

  return { descriptionKey: CATEGORY_DESCRIPTION_KEYS[transfer.category] ?? '', descriptionParams: {} }
}

export function useReceivableValues(
  paymentModelData: MaybeRefOrGetter<ReceiptModel | null | undefined>,
  options: UseReceivableValuesOptions = {},
): UseReceivableValuesResult {
  const data = computed(() => toValue(paymentModelData) ?? null)

  const isStepInAnalysis = computed(() => {
    const stepStatus = toValue(options.stepStatus)
    return !!stepStatus && STEP_STATUSES_IN_ANALYSIS.includes(stepStatus)
  })

  const isStepInProgress = computed(() => {
    const stepStatus = toValue(options.stepStatus)
    return !stepStatus || stepStatus === PaymentModelStatus.IN_PROGRESS
  })

  const partnerTransfers = computed<ReceiptTransfer[]>(() =>
    (data.value?.transfers ?? []).filter(transfer => transfer.recipient === 'partner'),
  )

  const visibleTransfers = computed(() => partnerTransfers.value.filter(transfer => transfer.value > 0))

  function getRowStatus(transfer: ReceiptTransfer): TransferVisualStatus {
    if (isStepInAnalysis.value && transfer.status === 'waiting_send_payment')
      return 'analysis'
    return getTransferVisualStatus(transfer.status)
  }

  const groups = computed<ReceivableGroup[]>(() => {
    const filledGroups = GROUP_DEFINITIONS
      .map(definition => ({
        definition,
        transfers: sortByCategory(visibleTransfers.value.filter(definition.matches)),
      }))
      .filter(group => group.transfers.length > 0)

    const financingCount = filledGroups
      .flatMap(group => group.transfers)
      .filter(transfer => transfer.category === 'financing')
      .length

    let number = 0
    let financingIndex = 0

    return filledGroups.map(({ definition, transfers }) => ({
      key: definition.key,
      labelKey: definition.labelKey,
      rows: transfers.map((transfer): ReceivableRow => {
        number += 1
        if (transfer.category === 'financing')
          financingIndex += 1

        const status = getRowStatus(transfer)

        return {
          id: transfer.id,
          number,
          transfer,
          value: transfer.value,
          status,
          ...getDescription(transfer, financingIndex, financingCount),
          canDownloadReceipt: status === 'paid',
        }
      }),
    }))
  })

  const rows = computed<ReceivableRow[]>(() => groups.value.flatMap(group => group.rows))

  const state = computed<ReceivablesPanelState>(() => {
    if (data.value?.is_vkit)
      return 'no-values'
    if (rows.value.length === 0 && isStepInProgress.value)
      return 'empty'
    return 'transfers'
  })

  const total = computed(() => sumValues(partnerTransfers.value))

  const totalDisplay = computed<ReceivablesTotalDisplay>(() => {
    if (isStepInAnalysis.value && rows.value.length === 0)
      return 'analysis'
    if (rows.value.length > 0 && rows.value.every(row => NEGATIVE_STATUSES.includes(row.status)))
      return 'struck'
    return 'value'
  })

  const financingDetails = computed<FinancingDetailsViewModel>(() => {
    const model = data.value
    const supplierName = toValue(options.supplierName)?.trim() || formatSupplierName(model?.supplier?.name)
    const hasSupplierName = supplierName !== ''
    const isReimbursable = !!toValue(options.isReimbursable)

    const equipmentValue = getEquipmentValue(model)
    const paidEquipmentTransfer = (model?.transfers ?? [])
      .find(transfer => transfer.category === 'equipment' && transfer.status === 'paid') ?? null
    const equipment: FinancingDetailsEquipmentLine | null = equipmentValue > 0
      ? {
          key: 'equipment',
          labelKey: getKitLabelKey(isReimbursable, hasSupplierName),
          labelParams: hasSupplierName ? { supplier: supplierName } : {},
          value: equipmentValue,
          receiptTransfer: paidEquipmentTransfer,
        }
      : null

    const financingTransfers = partnerTransfers.value.filter(transfer => transfer.category === 'financing')
    const commissionFinancing = Number(model?.commissions?.financing?.value) || 0
    const commissionInsurance = Number(model?.commissions?.insurance?.value) || 0

    const services: FinancingDetailsLine[] = [
      {
        key: 'services',
        labelKey: `${I18N_PREFIX}.details.services`,
        labelParams: {},
        value: financingTransfers.length ? sumValues(financingTransfers) : null,
      },
    ]

    if (commissionFinancing > 0) {
      services.push({
        key: 'commission_financing',
        labelKey: `${I18N_PREFIX}.details.commission_financing`,
        labelParams: {},
        value: commissionFinancing,
      })
    }

    if (commissionInsurance > 0) {
      services.push({
        key: 'commission_insurance',
        labelKey: `${I18N_PREFIX}.details.commission_insurance`,
        labelParams: {},
        value: commissionInsurance,
      })
    }

    const hasCommission = commissionFinancing > 0 || commissionInsurance > 0

    return {
      financingValue: model?.financing_value ?? null,
      equipment,
      servicesTitleKey: hasCommission
        ? `${I18N_PREFIX}.details.my_commissions_and_services`
        : `${I18N_PREFIX}.details.my_services`,
      services,
    }
  })

  return {
    state,
    groups,
    rows,
    total,
    totalDisplay,
    isStepInAnalysis,
    financingDetails,
  }
}
